/**
 * useMailboxes Hooks
 * TanStack Query hooks for EdgeMail Mailbox Management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EdgeMailAdminAPI } from '../services/EdgeMailAdminAPI';
import type {
  MailboxFilters,
  CreateMailboxRequest,
  UpdateMailboxRequest,
  CreateSendIdentityRequest,
  SendIdentityStatus,
} from '../types/admin.types';
import { adminQueryKeys } from './useAdminDashboard';

// Mailbox query keys
export const mailboxQueryKeys = {
  all: [...adminQueryKeys.all, 'mailboxes'] as const,
  list: (filters?: MailboxFilters) => [...mailboxQueryKeys.all, 'list', filters] as const,
  detail: (id: string) => [...mailboxQueryKeys.all, 'detail', id] as const,
  domains: () => [...adminQueryKeys.all, 'domains'] as const,
  keycloakUsers: (search?: string) => [...adminQueryKeys.all, 'keycloak-users', search] as const,
  sendIdentities: (mailboxId: string) => [...mailboxQueryKeys.detail(mailboxId), 'send-identities'] as const,
};

/**
 * Hook to fetch paginated list of mailboxes
 */
export function useMailboxList(filters?: MailboxFilters) {
  return useQuery({
    queryKey: mailboxQueryKeys.list(filters),
    queryFn: () => EdgeMailAdminAPI.getMailboxes(filters),
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single mailbox
 */
export function useMailbox(mailboxId: string) {
  return useQuery({
    queryKey: mailboxQueryKeys.detail(mailboxId),
    queryFn: () => EdgeMailAdminAPI.getMailbox(mailboxId),
    enabled: !!mailboxId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch domains for dropdown
 */
export function useDomains() {
  return useQuery({
    queryKey: mailboxQueryKeys.domains(),
    queryFn: () => EdgeMailAdminAPI.getDomains(),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch Keycloak users for mailbox linking
 */
export function useKeycloakUsers(options?: { search?: string; withoutMailbox?: boolean }) {
  return useQuery({
    queryKey: mailboxQueryKeys.keycloakUsers(options?.search),
    queryFn: () => EdgeMailAdminAPI.getKeycloakUsers(options),
    staleTime: 60000,
  });
}

/**
 * Hook to create a new mailbox
 */
export function useCreateMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMailboxRequest) => EdgeMailAdminAPI.createMailbox(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.metrics() });
    },
  });
}

/**
 * Hook to update a mailbox
 */
export function useUpdateMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mailboxId, data }: { mailboxId: string; data: UpdateMailboxRequest }) =>
      EdgeMailAdminAPI.updateMailbox(mailboxId, data),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.detail(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.list() });
    },
  });
}

/**
 * Hook to suspend a mailbox
 */
export function useSuspendMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mailboxId: string) => EdgeMailAdminAPI.suspendMailbox(mailboxId),
    onSuccess: (_, mailboxId) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.detail(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.metrics() });
    },
  });
}

/**
 * Hook to reactivate a mailbox
 */
export function useReactivateMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mailboxId: string) => EdgeMailAdminAPI.reactivateMailbox(mailboxId),
    onSuccess: (_, mailboxId) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.detail(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.metrics() });
    },
  });
}

/**
 * Hook to delete a mailbox
 */
export function useDeleteMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mailboxId: string) => EdgeMailAdminAPI.deleteMailbox(mailboxId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.metrics() });
    },
  });
}

/**
 * Hook to update mailbox quota
 */
export function useUpdateMailboxQuota() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mailboxId, quotaBytes }: { mailboxId: string; quotaBytes: number }) =>
      EdgeMailAdminAPI.updateMailboxQuota(mailboxId, quotaBytes),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.detail(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.list() });
    },
  });
}

export function useSendIdentities(mailboxId: string, enabled = true) {
  return useQuery({
    queryKey: mailboxQueryKeys.sendIdentities(mailboxId),
    queryFn: () => EdgeMailAdminAPI.listSendIdentities(mailboxId),
    enabled: enabled && !!mailboxId,
    staleTime: 30000,
  });
}

export function useCreateSendIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mailboxId, data }: { mailboxId: string; data: CreateSendIdentityRequest }) =>
      EdgeMailAdminAPI.createSendIdentity(mailboxId, data),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.sendIdentities(mailboxId) });
    },
  });
}

export function useVerifySendIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mailboxId, identityId }: { mailboxId: string; identityId: string }) =>
      EdgeMailAdminAPI.verifySendIdentity(mailboxId, identityId),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.sendIdentities(mailboxId) });
    },
  });
}

export function useUpdateSendIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mailboxId,
      identityId,
      data,
    }: {
      mailboxId: string;
      identityId: string;
      data: { isDefault?: boolean; status?: SendIdentityStatus; replyMatchingEnabled?: boolean; displayName?: string | null };
    }) => EdgeMailAdminAPI.updateSendIdentity(mailboxId, identityId, data),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.sendIdentities(mailboxId) });
    },
  });
}

export function useDeleteSendIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mailboxId, identityId }: { mailboxId: string; identityId: string }) =>
      EdgeMailAdminAPI.deleteSendIdentity(mailboxId, identityId),
    onSuccess: (_, { mailboxId }) => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.sendIdentities(mailboxId) });
    },
  });
}
